import * as React from 'react';
import { SetStateAction } from 'react';
import ViewHeader from '../../../widgets/ViewHeader';
import Panel from '../../../widgets/Panel';
import Viewport from '../../../widgets/Viewport';
import { Button, Col, Input, Progress, Row } from 'reactstrap';
import './AgentEndpoint.scss';
import { OptionType } from '../../../types';
import { useDispatch, useSelector } from 'react-redux';
import LoadingIndicator from '../../../widgets/LoadingIndicator';
import ErrorJumbotron from '../../../widgets/ErrorJumbotron';
import {
  AGENT_ENDPOINT_PAGE_SIZE,
  DARK_PLACEHOLDER,
  getNewValue,
  ROUTE_PATH,
  SELECT_STYLE,
} from '../../../constants';
import { apiRequest } from '../../../APIClient';
import InputDropdown from '../../../widgets/InputDropdown';
import { ValueType } from 'react-select/src/types';
import {
  AgentEndpointPager,
  AgentEndpointTable,
  AgentExportButton,
  useTableSort,
} from './AgentEndpointWidgets';
import ClearFiltersButton from '../../../widgets/ClearFiltersButton';
import { PageTitle } from '../../../widgets/PageTitle';
import { useInterval } from '../../../hooks';
import { riskSelector } from '../../../reducers/risk';
import { memoizedAgentSelectors } from '../../../selectors';
import LightTooltip, {
  TOOLTIP_DELAY_INSTANT_SHOW_INSTANT_HIDE,
} from '../../../widgets/LightTooltip';
import Icon from '../../../widgets/Icons';
import { APIError } from '../../../APIError';
import { sessionSelector } from '../../../reducers/session';

const MISSING_DATA_EXPLANATORY_TOOLTIP = (
  <LightTooltip
    size="lg"
    tooltip={
      <div className="p-3">
        The Agent collects endpoint audit information every 30 minutes and
        relays this to the Arctic Wolf Portal and as such, does not necessarily
        reflect current state. There are known cases where certain data fields
        may contain a &lt;blank&gt;:
        <ul>
          <li>
            Operating System language packs other than English are not supported
            at this time
          </li>
          <li>
            Atypical network configurations may prevent the Agent audit data
            from being relayed to the Arctic Wolf Portal
          </li>
          <li>
            Changing network adapters or IP states may impact the accuracy of
            the reported IP and MAC data fields
          </li>
          <li>
            Last User may be reported &lt;blank&gt; if the Endpoint is online
            without a user logged in
          </li>
        </ul>
        Please contact your CST with any questions.
      </div>
    }
    delay={TOOLTIP_DELAY_INSTANT_SHOW_INSTANT_HIDE}
    placement="bottom"
    flip={false}
    data-cy="agent-help-tooltip"
  >
    <span className="agent-help">
      Help with this view
      <Icon.Question size={14} className="ml-2 mr-4" />
    </span>
  </LightTooltip>
);

type TimeoutErrorJumbotronProps = {
  onRetry: () => void;
  error: APIError;
};

/**
 * An error jumbotron with a retry button
 */
export const RetryableErrorJumbotron = ({
  error,
  onRetry,
}: TimeoutErrorJumbotronProps) => {
  const message = (
    <>
      {error.message}
      <Button
        className="ml-3"
        size="lg"
        outline
        color="primary"
        style={{ fontSize: '14px' }}
        onClick={onRetry}
      >
        Retry
      </Button>
    </>
  );

  return (
    <ErrorJumbotron
      error={error}
      className="vertical-center"
      message={message}
    />
  );
};

type NullableValue = OptionType | null;
type VT = ValueType<OptionType>;

/**
 * transform a string to a minimum OptionType object used by react-select
 */
const makeOption = (value: string): OptionType => ({ label: value, value });

/**
 * transform an array of strings to an array of OptionType objects used by react-select
 */
const makeOptions = (values: string[]): OptionType[] =>
  values.map((value) => makeOption(value));

/**
 * a custom hook built for single selects which calls useState and
 *   builds an onChange callback that can be directly used by SingleSelect
 */
const useSingleSelect = (
  init: NullableValue = null,
): [
  NullableValue,
  React.Dispatch<SetStateAction<NullableValue>>,
  (newValue: ValueType<OptionType>) => void,
] => {
  const [value, setValue] = React.useState<NullableValue>(init);
  const onChange = React.useCallback(
    (value: VT) => {
      const v = getNewValue(value, false);
      setValue(v);
    },
    [setValue],
  );
  return [value, setValue, onChange];
};

const STATUS_OPTIONS = makeOptions(['Online', 'Offline']);
const CONTAINMENT_OPTIONS = makeOptions(['Contained', 'Not contained']);

export const POLLING_INTERVAL = 3_000;
export const POLLING_TIMEOUT = 30_000;

/**
 * all dispatch (i.e. API) usage of the component
 */
const useUpdateAgents = () => {
  const totalRowCount = useSelector(
    memoizedAgentSelectors.selectNumberOfAgents,
  );
  const completeRows = useSelector(
    memoizedAgentSelectors.selectCompleteAgentRows,
  );
  const incompleteIDs = useSelector(
    memoizedAgentSelectors.selectIncompleteAgentIDs,
  );
  const isLoading = useSelector(riskSelector.isLoadingAgents);

  // initial fetch on mount
  const dispatch = useDispatch();
  React.useEffect(() => {
    if (totalRowCount === 0 && !isLoading) {
      dispatch(apiRequest.getAgentList());
    }
    // eslint-disable-next-line
  }, []);

  // set a max time out for polling
  const [hasTimedOut, setHasTimedOut] = React.useState(false);
  const missingCountRef = React.useRef(incompleteIDs.length);
  missingCountRef.current = incompleteIDs.length;
  React.useEffect(() => {
    setTimeout(() => {
      setHasTimedOut(true);
      if (missingCountRef.current > 0) {
        const message = `${missingCountRef.current} missing endpoint${
          missingCountRef.current > 1 ? 's' : ''
        }. Try refreshing the page to load the missing endpoints.`;
        dispatch(apiRequest.setGlobalStatus(message, 'warning'));
      }
    }, POLLING_TIMEOUT);
  }, [dispatch]);

  // incremental loading of incomplete agents
  const shouldPoll = !hasTimedOut && !isLoading && incompleteIDs.length > 0;
  const interval = shouldPoll && POLLING_INTERVAL;

  const callback = React.useCallback(() => {
    dispatch(apiRequest.getAgentsByIds(incompleteIDs));
  }, [incompleteIDs, dispatch]);
  useInterval(callback, interval);

  // return a progress which should only be <100 when it's actively polling
  const progress =
    !hasTimedOut && totalRowCount > 0
      ? Math.floor((completeRows.length / totalRowCount) * 100)
      : 100;
  return { progress };
};

const AgentEndpointView: React.FunctionComponent = () => {
  const error = useSelector(riskSelector.getAgentsError);
  const isLoading = useSelector(riskSelector.isLoadingAgents);
  const agentRows = useSelector(memoizedAgentSelectors.selectAgentRowsForTable);
  const showMissingDataTooltip = useSelector(
    memoizedAgentSelectors.selectTableHasBlanks,
  );
  const totalRowCount = useSelector(
    memoizedAgentSelectors.selectNumberOfAgents,
  );
  const containedRowCount = useSelector(
    memoizedAgentSelectors.selectNumberOfContainedAgents,
  );

  const orgID = useSelector(sessionSelector.getRiskID);
  const orgIDParam = orgID ? `?id=${orgID}` : '';

  const dispatch = useDispatch();
  const fetchAgents = React.useCallback(() => {
    dispatch(apiRequest.getAgentList());
  }, [dispatch]);

  let banner: React.ReactNode = null;
  const isInit = isLoading && totalRowCount === 0;
  if (isInit) {
    // on subsequent polling we don't want to show the spinner
    banner = (
      <LoadingIndicator size="lg" centered>
        Loading endpoint data...
      </LoadingIndicator>
    );
  }
  if (error) {
    banner = <RetryableErrorJumbotron error={error} onRetry={fetchAgents} />;
  }

  const { progress } = useUpdateAgents();
  const isPolling = progress < 100;
  const inputDisabled = isPolling || isInit;

  const [
    containmentFilterValue,
    setContainmentFilter,
    onContainmentFilterChange,
  ] = useSingleSelect();

  const OSFilterOptions = React.useMemo(() => {
    // de-dupe
    const set = new Set(
      agentRows.map((agent) => agent.OSVersion).filter((version) => version),
    );
    return makeOptions(Array.from(set));
  }, [agentRows]);

  // 0-based: the first row being displayed will be filteredAgents[index]
  const [index, setIndex] = React.useState(0);
  const onIndexChange = React.useCallback(
    (event: React.SyntheticEvent<HTMLButtonElement>) => {
      const newIndex = event.currentTarget.getAttribute('index');
      if (newIndex) {
        setIndex(parseInt(newIndex));
      }
    },
    [setIndex],
  );

  const [statusFilterValue, setStatusFilter, onStatusFilterChange] =
    useSingleSelect();
  const [osFilterValue, setOSFilter, onOSFilterChange] = useSingleSelect();

  const [textFilterValue, setTextFilter] = React.useState('');
  const onTextFilterChange = React.useCallback(
    (event: React.SyntheticEvent<HTMLInputElement>) => {
      setTextFilter(event.currentTarget.value);
    },
    [setTextFilter],
  );

  const filteredAgents = React.useMemo(() => {
    let filteredList = agentRows;
    if (statusFilterValue) {
      filteredList = filteredList.filter(
        (agent) => agent.status === statusFilterValue.value,
      );
    }
    if (osFilterValue) {
      filteredList = filteredList.filter(
        (agent) => agent.OSVersion === osFilterValue.value,
      );
    }
    if (textFilterValue) {
      filteredList = filteredList.filter((agent) =>
        agent._flat.includes(textFilterValue.toLocaleLowerCase()),
      );
    }
    if (containmentFilterValue) {
      filteredList = filteredList.filter(
        (agent) => agent.contained === containmentFilterValue.value,
      );
    }
    return filteredList;
  }, [
    agentRows,
    statusFilterValue,
    osFilterValue,
    textFilterValue,
    containmentFilterValue,
  ]);

  const onClearAllFilters = React.useCallback(() => {
    setStatusFilter(null);
    setOSFilter(null);
    setTextFilter('');
    setContainmentFilter(null);
  }, [setStatusFilter, setOSFilter, setTextFilter, setContainmentFilter]);

  const isFiltering = !!(
    statusFilterValue ||
    osFilterValue ||
    textFilterValue ||
    containmentFilterValue
  );

  // reset the page on any filter change
  React.useEffect(() => {
    setIndex(0);
  }, [setIndex, filteredAgents]);

  const { headerProps, sortedAgents } = useTableSort(
    filteredAgents,
    'contained',
  );

  const visibleSortedAgents = sortedAgents.slice(
    index,
    index + AGENT_ENDPOINT_PAGE_SIZE,
  );

  // during polling, pagers are hidden
  const tablePager = visibleSortedAgents.length > 0 && (
    <AgentEndpointPager
      data-cy="agent-endpoints-pager"
      index={index}
      total={filteredAgents.length}
      onIndexChange={onIndexChange}
      isLoading={isPolling}
    />
  );

  // during polling, export is disabled
  const exportButton = isPolling ? (
    <AgentExportButton disabled tooltip="Not available during loading" />
  ) : (
    <AgentExportButton />
  );

  const progressBar = isPolling && (
    <div className="agent-endpoints-progress">
      <Progress animated value={progress}>
        {progress}%
      </Progress>
    </div>
  );

  const tableHeader = (
    <div className="d-flex mb-4">
      {tablePager}
      {progressBar}
      <div className="flex-grow text-right">
        {showMissingDataTooltip && MISSING_DATA_EXPLANATORY_TOOLTIP}
        {visibleSortedAgents.length > 0 && exportButton}
      </div>
    </div>
  );

  const tableFooter = isPolling ? (
    <LoadingIndicator size="md" className="d-flex justify-content-center my-4">
      Loading...
    </LoadingIndicator>
  ) : (
    <>
      <div className="d-flex my-4">{tablePager}</div>
      <div className="text-center">
        Endpoints must have the{' '}
        <a
          href={ROUTE_PATH.DOWNLOADS.UNIFIED_PORTAL + orgIDParam}
          target="_blank"
          rel="noopener noreferrer"
        >
          Endpoint Agent
        </a>{' '}
        installed in order to view their status.
      </div>
    </>
  );

  const panelMinHeight = showMissingDataTooltip
    ? 'agent-min-height-for-tooltips'
    : 'min-height-for-loading-indicator';

  return (
    <Viewport>
      <PageTitle title="Endpoint Status" />
      <ViewHeader title="Endpoint Status" />

      <Panel>
        <Row className="agent-endpoints-filter-row">
          <Col xs={12} sm={6} md={3}>
            <h5>Status</h5>
            <InputDropdown
              data-cy="agent-endpoints-status-filter"
              styles={inputDisabled ? SELECT_STYLE : DARK_PLACEHOLDER}
              showDropdownArrow={true}
              options={STATUS_OPTIONS}
              value={statusFilterValue}
              onChange={onStatusFilterChange}
              placeholder="All statuses"
              isSearchable={false}
              isDisabled={inputDisabled}
            />
          </Col>
          <Col xs={12} sm={6} md={3}>
            <h5>Operating System</h5>
            <InputDropdown
              data-cy="agent-endpoints-os-filter"
              styles={inputDisabled ? SELECT_STYLE : DARK_PLACEHOLDER}
              showDropdownArrow={true}
              options={OSFilterOptions}
              value={osFilterValue}
              onChange={onOSFilterChange}
              placeholder="All operating systems"
              isDisabled={inputDisabled}
            />
          </Col>
          <Col xs={12} sm={6} md={3}>
            <h5>Containment</h5>
            <InputDropdown
              data-cy="agent-endpoints-containment-filter"
              styles={
                containedRowCount === 0 || inputDisabled
                  ? SELECT_STYLE
                  : DARK_PLACEHOLDER
              }
              showDropdownArrow={true}
              options={CONTAINMENT_OPTIONS}
              value={containmentFilterValue}
              onChange={onContainmentFilterChange}
              placeholder={
                containedRowCount > 0
                  ? 'All containment types'
                  : 'Not Contained'
              }
              isDisabled={containedRowCount === 0 || inputDisabled}
            />
          </Col>
          <Col xs={12} sm={6} md={3}>
            <h5>Filter</h5>
            <Input
              data-cy="agent-endpoints-text-filter"
              value={textFilterValue}
              onChange={onTextFilterChange}
              placeholder="Type to filter"
              disabled={inputDisabled}
            />
          </Col>
        </Row>

        {isFiltering && (
          <Row className="agent-endpoints-clear-filter">
            <Col>
              <ClearFiltersButton onClick={onClearAllFilters} />
            </Col>
          </Row>
        )}
      </Panel>

      <div className="h3 mb-4">
        {isFiltering ? 'Matching endpoints' : 'Endpoints'}
        <span className="font-weight-normal ml-2">
          ({isFiltering && `${filteredAgents.length} of `}
          {inputDisabled ? 'Loading...' : agentRows.length})
        </span>
      </div>

      <Panel className={`${panelMinHeight} horizontal-overflow`}>
        {banner || (
          <>
            {tableHeader}
            <AgentEndpointTable
              total={agentRows.length}
              agents={visibleSortedAgents}
              headerProps={headerProps}
              textFilterValue={textFilterValue}
            />
            {tableFooter}
          </>
        )}
      </Panel>
    </Viewport>
  );
};

export default AgentEndpointView;
